import React from 'react';
import { tokenize } from '../lib/richText';
import { toBlocks } from '../lib/richBlocks';
import useIsPhone from '../lib/useIsPhone';

/**
 * Render the small amount of markdown the assistant emits.
 *
 * Model output reaches this component, so it is mapped to React children and NEVER to
 * dangerouslySetInnerHTML: markup in an answer renders as the text it is, not as markup. The
 * tokenizer only ever produces http(s) URLs, so no `javascript:` href can be constructed here.
 *
 * <h2>Blocks, and why the measure is capped</h2>
 * The assistant writes headings and lists. Until this component understood them, every answer
 * rendered as one continuous run of text - the structure was in the words and nowhere in the
 * layout, so the reader had to rebuild it. Two changes fix that, and both come straight from how
 * people actually read on screens:
 *
 *  - Scanning beats reading. Subheads and list items become real blocks with real spacing and a
 *    hanging indent, so the eye can find the next item without reading the previous one.
 *  - Line length. Comfortable measure is about 45-75 characters; these bubbles were running at
 *    well over a hundred on a wide screen, which is where the eye starts losing its place on the
 *    return sweep. Prose is capped at `65ch` here. The BUBBLE stays as wide as it was - only the
 *    text column is constrained - so nothing about the layout moves.
 */
function Inline({ text }) {
  const isPhone = useIsPhone();
  return (
    <>
      {tokenize(text).map((t, i) => {
        if (t.type === 'bold') {
          return (
            <strong key={i} className="font-semibold text-foreground">
              {t.value}
            </strong>
          );
        }
        if (t.type === 'code') {
          return (
            <code
              key={i}
              className="rounded bg-black/30 px-1 py-0.5 font-mono text-[0.9em] break-all"
            >
              {t.value}
            </code>
          );
        }
        if (t.type === 'link') {
          /*
           * A cited source has to be reachable in one click - that is the whole point of citing it.
           *
           * New tab on desktop and tablet, same tab on a phone: on a large screen the customer is
           * mid-conversation and losing the chat to a navigation is the wrong trade, while on a
           * phone background tabs are awkward to find and the back button is the natural way home.
           *
           * rel is not optional. These are third-party pages the server found, so noopener denies
           * them window.opener and noreferrer stops the dashboard URL leaking in the Referer.
           *
           * title is the destination, always. The tokenizer already refuses the addresses that
           * lie outright (see richText.js), but a label is still free text written by a model
           * that is fed fetched web content, and this is the cheapest way for a customer to
           * check where a citation actually goes - it works on a tablet, where there is no
           * status bar to hover a link into.
           */
          return (
            <a
              key={i}
              href={t.url}
              title={t.url}
              target={isPhone ? '_self' : '_blank'}
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2 hover:text-primary/80 break-all"
            >
              {t.value}
            </a>
          );
        }
        return <React.Fragment key={i}>{t.value}</React.Fragment>;
      })}
    </>
  );
}

export default function RichText({ text }) {
  const blocks = toBlocks(text);
  if (!blocks.length) {
    return null;
  }

  return (
    // 65ch keeps the measure inside the comfortable range on a wide screen without narrowing the
    // bubble itself; on a phone the bubble is already narrower than 65ch, so this changes nothing.
    <div className="max-w-[65ch] space-y-2">
      {blocks.map((b, i) => {
        if (b.type === 'heading') {
          return (
            <p
              key={i}
              // Extra space ABOVE a heading, none below: the gap is what tells a scanning eye a
              // new group has started, and it belongs between groups rather than inside one.
              className="pt-1 text-[0.95em] font-semibold text-foreground first:pt-0"
            >
              <Inline text={b.value} />
            </p>
          );
        }
        if (b.type === 'code') {
          /*
           * A fenced block, rendered verbatim: no inline tokenizing runs over it, which is the
           * whole reason it is a block type. A pasted log or stack trace routinely contains `**`
           * and bare URLs, and reading those as emphasis and links turned the one kind of text
           * whose exact characters matter into something that no longer said what was pasted.
           *
           * It scrolls in its own box rather than wrapping - a wrapped log line is a log line
           * you cannot line up against the one above it - and the box is what stops a long line
           * widening the bubble around it.
           */
          return (
            <pre
              key={i}
              className="overflow-x-auto rounded-md bg-black/30 p-2 font-mono text-[0.85em] leading-snug text-foreground"
            >
              {b.value}
            </pre>
          );
        }
        if (b.type === 'item') {
          return (
            // Hanging indent: the marker sits in the margin so every item's text starts on the
            // same vertical line and the column can be scanned without reading it.
            <p key={i} className="relative pl-4 leading-relaxed">
              <span aria-hidden className="absolute left-0 top-0 text-muted-foreground">
                &bull;
              </span>
              <Inline text={b.value} />
            </p>
          );
        }
        return (
          <p key={i} className="leading-relaxed">
            <Inline text={b.value} />
          </p>
        );
      })}
    </div>
  );
}
